import React from 'react';
import { Box, Typography } from '@mui/material';

const GREEN = '#0F6E56';
const RED = '#993C1D';

/**
 * Small side table displayed to the right of each gauge card.
 * Rows: Overall (PTD), This week, Last week, Trend.
 *
 * Props:
 *   overall   — number (e.g. 1.271)
 *   thisWeek  — number
 *   lastWeek  — number | null
 *   trend     — number | null  (positive = up arrow, negative = down arrow)
 *   goodDirection — 'below' (default: under 1.0 and falling are good) or
 *                   'above' (over 1.0 and rising are good), ticket #264
 */
const MetricSideTable = ({
  overall,
  thisWeek,
  lastWeek,
  trend,
  goodDirection = 'below',
}) => {
  const aboveIsGood = goodDirection === 'above';
  const fmt = (v) => (v != null && Number.isFinite(v) ? v.toFixed(3) : '—');

  const valueColor = (v) => {
    if (v == null || !Number.isFinite(v)) return 'text.primary';
    const isGood = aboveIsGood ? v >= 1.0 : v <= 1.0;
    return isGood ? GREEN : RED;
  };

  const trendText = (v) => {
    if (v == null || !Number.isFinite(v)) return '—';
    const arrow = v < 0 ? '▼' : '▲';
    return `${arrow} ${Math.abs(v).toFixed(3)}`;
  };

  const trendColor = (v) => {
    if (v == null || !Number.isFinite(v)) return 'text.secondary';
    const isGood = aboveIsGood ? v > 0 : v < 0;
    return isGood ? GREEN : RED;
  };

  const rows = [
    { label: 'Overall (PTD)', value: overall, color: valueColor(overall) },
    { label: 'This week', value: thisWeek, color: valueColor(thisWeek) },
    { label: 'Last week', value: lastWeek, color: valueColor(lastWeek) },
    {
      label: 'Trend',
      value: null,
      display: trendText(trend),
      color: trendColor(trend),
      valueSize: '12px',
    },
  ];

  return (
    <Box sx={{ flex: 1, pt: 0.5 }}>
      {rows.map((r) => (
        <Box
          key={r.label}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 0.5,
          }}
        >
          <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
            {r.label}
          </Typography>
          <Typography
            sx={{
              fontSize: r.valueSize ?? '14px',
              fontWeight: 500,
              color: r.color,
            }}
          >
            {r.display ?? fmt(r.value)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export default MetricSideTable;
